
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import "flatpickr/dist/themes/material_blue.css";

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
    Legend,
    AreaChart,
    Area,
    Cell,
    RadialBarChart,
    RadialBar,
    PieChart,
    Pie,
} from "recharts";

import PreloadFetch from "../components/PreloadFetch";
import PhoneLoader from "../components/loader/PhoneLoader";
import ScrollToTop from "../components/ScrollTop";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../provider/authProvider";

import "../FmStats.css";

const API = process.env.REACT_APP_API || "";

const PEN = new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const COLORS = ["#2563eb", "#16a34a", "#f97316", "#9333ea", "#dc2626", "#0891b2", "#ca8a04", "#db2777"];
const MORA_COLORS = { semanal: "#2563eb", quincenal: "#f97316", mensual: "#dc2626" };

const ORDER_OPTIONS = [
    { value: "total_fms_desc", label: "FMs: mayor a menor" },
    { value: "total_fms_asc", label: "FMs: menor a mayor" },
    { value: "amount_desc", label: "Monto: mayor a menor" },
    { value: "amount_asc", label: "Monto: menor a mayor" },
    { value: "ticket_desc", label: "Ticket promedio: mayor a menor" },
    { value: "ticket_asc", label: "Ticket promedio: menor a mayor" },
];

const initialFilters = {
    advisorId: null,
    city: null,
    sedeId: null,
    segment: null,
    orderBy: ORDER_OPTIONS[0],
    dateRange: [],
};

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 44,
        borderRadius: 14,
        borderColor: state.isFocused ? "#2563eb" : "#dbe3ef",
        boxShadow: state.isFocused ? "0 0 0 4px rgba(37, 99, 235, .12)" : "none",
        ":hover": { borderColor: "#2563eb" },
    }),
    menu: (base) => ({ ...base, zIndex: 9999, borderRadius: 14, overflow: "hidden" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const getToday = () => formatDate(new Date());
const getMonthStart = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const normalizeChartName = (name) => {
    const text = String(name || "Sin asesor").trim();
    return text.length <= 18 ? text : `${text.slice(0, 18)}...`;
};

const getPercentText = (value) => `${Number(value || 0).toFixed(2)}% de la cartera`;

const renderCustomizedPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.62;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const label = String(name || "");

    return (
        <text x={x} y={y} fill="#0f172a" textAnchor={x > cx ? "start" : "end"} dominantBaseline="central" fontSize={12} fontWeight={700}>
            {`${label.slice(0, 12)} ${((percent || 0) * 100).toFixed(1)}%`}
        </text>
    );
};

const KpiCard = ({ title, value, subValue, variant, badge }) => (
    <div className={`fmKpiCard ${variant || ""}`}>
        <div className="fmKpiHead">
            <span>{title}</span>
            {badge ? <em>{badge}</em> : null}
        </div>
        <strong>{value}</strong>
        {subValue ? <small>{subValue}</small> : null}
    </div>
);

const EmptyChart = ({ text = "No hay información para mostrar con los filtros seleccionados." }) => (
    <div className="fmEmptyChart"><span>Sin datos</span><p>{text}</p></div>
);

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="fmChartTooltip">
            <strong>{label}</strong>
            {payload.map((item) => {
                const key = String(item?.dataKey || "");
                const name = String(item?.name || "");
                const value = Number(item?.value || 0);
                const isMoney = key.toLowerCase().includes("amount") || key.toLowerCase().includes("monto") || name.toLowerCase().includes("monto") || name.toLowerCase().includes("ticket");
                return <p key={`${item.dataKey}-${item.name}`}>{item.name}: {isMoney ? PEN.format(value) : value}</p>;
            })}
        </div>
    );
};

const MoraParticipationTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const row = payload?.[0]?.payload || {};
    const title = row.sede || row.advisor_name || row.name || row.city || label || "Sin registro";
    const totalAmount = Number(row.total_amount || 0);
    const overdueAmount = Number(row.overdue_amount || 0);
    const paidAmount = Number(row.paid_amount || 0);
    const overduePercent = Number(row.overdue_percent || 0);
    const paidPercent = Number(row.paid_percent || 0);

    return (
        <div className="fmChartTooltip fmMoraParticipationTooltip">
            <strong>{title}</strong>
            <p>Total FM: {PEN.format(totalAmount)}</p>
            <p>Mora: {PEN.format(overdueAmount)} ({overduePercent.toFixed(2)}%)</p>
            <p>Pagado: {PEN.format(paidAmount)} ({paidPercent.toFixed(2)}%)</p>
        </div>
    );
};


const ParticipationResume = ({ items = [] }) => (
    <div className="fmMoraResumeList">
        {items.slice(0, 8).map((item, index) => (
            <div className="fmMoraResumeItem" key={`${item.name || item.sede || item.city || item.advisor_name}-${index}`}>
                <strong>{item.sede || item.city || item.advisor_name || item.name}</strong>
                <div>
                    <span className="moraText">Mora: {PEN.format(Number(item.overdue_amount || 0))} <b>({Number(item.overdue_percent || 0).toFixed(2)}%)</b></span>
                    <span className="paidText">Pagado: {PEN.format(Number(item.paid_amount || 0))} <b>({Number(item.paid_percent || 0).toFixed(2)}%)</b></span>
                </div>
            </div>
        ))}
    </div>
);

const getRiskClass = (risk) => `risk-${risk?.key || "low"}`;

const CommitmentForm = ({ client, onSave }) => {
    const [date, setDate] = useState("");
    const [amount, setAmount] = useState(client?.overdueAmount || 0);
    const [note, setNote] = useState("");
    const [open, setOpen] = useState(false);

    const submit = async () => {
        if (!date) return;
        await onSave({ fmId: client.fmId, commitmentDate: date, commitmentAmount: amount, note });
        setOpen(false);
        setNote("");
    };

    return (
        <div className="moraCommitmentBox">
            <button type="button" className="moraSmallBtn" onClick={() => setOpen((v) => !v)}>
                {open ? "Cerrar" : "Registrar compromiso"}
            </button>
            {open ? (
                <div className="moraCommitmentForm">
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                    <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Monto" />
                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota de seguimiento" />
                    <button type="button" onClick={submit}>Guardar</button>
                </div>
            ) : null}
        </div>
    );
};

export default function FmStats() {
    const { user, rol } = useAuth();
    const userRole = String(rol || "");
    const isAdvisorRole = userRole == "3";
    const calendarInputRef = useRef(null);
    const calendarInstanceRef = useRef(null);
    const [filters, setFilters] = useState(initialFilters);
    const [data, setData] = useState(null);
    const [mora, setMora] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [savingCommitment, setSavingCommitment] = useState(false);

    const token = localStorage.getItem("token");

    const api = useMemo(() => axios.create({
        baseURL: API,
        timeout: 30000,
        headers: { Authorization: `Bearer ${token || ""}` },
        withCredentials: true,
    }), [token]);

    const handleChange = useCallback((name, value) => setFilters((prev) => ({ ...prev, [name]: value })), []);

    const params = useMemo(() => {
        const query = {};

        if (isAdvisorRole) {
            return query;
        }

        if (filters.dateRange?.length === 2) {
            query.startDate = formatDate(filters.dateRange[0]);
            query.endDate = formatDate(filters.dateRange[1]);
        }
        if (filters.advisorId?.value) query.advisorId = filters.advisorId.value;
        if (filters.city?.value) query.city = filters.city.value;
        if (filters.sedeId?.value) query.sedeId = filters.sedeId.value;
        if (filters.segment?.value) query.segment = filters.segment.value;
        if (filters.orderBy?.value) query.orderBy = filters.orderBy.value;
        return query;
    }, [filters, isAdvisorRole]);

    const fetchStats = useCallback(async () => {
        try {
            setLoading(true);
            const [statsResponse, moraResponse] = await Promise.all([
                api.get("/fm-stats", { params }),
                api.get("/fm-stats/mora", { params }),
            ]);
            setData(statsResponse?.data || {});
            setMora(moraResponse?.data || {});
        } catch (error) {
            console.error("fetchStats error:", error);
            setData(null);
            setMora(null);
        } finally {
            setLoading(false);
        }
    }, [api, params]);

    useEffect(() => { fetchStats(); }, [fetchStats]);

    useEffect(() => {
        if (isAdvisorRole) {
            try { calendarInstanceRef.current?.destroy?.(); } catch (error) { console.warn("flatpickr destroy error:", error); }
            calendarInstanceRef.current = null;
            return undefined;
        }

        flatpickr.localize(Spanish);
        if (calendarInputRef.current && !calendarInstanceRef.current) {
            calendarInstanceRef.current = flatpickr(calendarInputRef.current, {
                mode: "range",
                dateFormat: "Y-m-d",
                locale: Spanish,
                allowInput: true,
                defaultDate: [getMonthStart(), getToday()],
                onChange: (selectedDates) => {
                    if (!selectedDates || selectedDates.length === 0) return handleChange("dateRange", []);
                    if (selectedDates.length === 2) handleChange("dateRange", selectedDates);
                },
            });
        }
        return () => {
            try { calendarInstanceRef.current?.destroy?.(); } catch (error) { console.warn("flatpickr destroy error:", error); }
            calendarInstanceRef.current = null;
        };
    }, [handleChange, isAdvisorRole]);

    const resetFilters = () => {
        if (isAdvisorRole) return;
        setFilters(initialFilters);
        if (calendarInstanceRef.current) calendarInstanceRef.current.setDate([getMonthStart(), getToday()], true);
    };

    const exportExcel = async () => {
        try {
            setExporting(true);
            const response = await api.get("/fm-stats/export", { params, responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "estadisticas-fm.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("exportExcel error:", error);
        } finally {
            setExporting(false);
        }
    };

    const saveCommitment = async (payload) => {
        try {
            setSavingCommitment(true);
            await api.post("/fm-stats/mora/commitment", payload);
            await fetchStats();
        } catch (error) {
            console.error("saveCommitment error:", error);
        } finally {
            setSavingCommitment(false);
        }
    };

    const advisors = useMemo(() => (data?.advisors || []).map((item) => ({
        ...item,
        advisor_chart_name: normalizeChartName(item.advisor_name),
        total_fms: Number(item.total_fms || 0),
        total_amount: Number(item.total_amount || 0),
        avg_ticket: Number(item.avg_ticket || 0),
        participation_amount: Number(item.participation_amount || 0),
        participation_qty: Number(item.participation_qty || 0),
        overdue_amount: Number(item.overdue_amount || 0),
        paid_amount: Number(item.paid_amount || 0),
        overdue_percent: Number(item.overdue_percent || 0),
        paid_percent: Number(item.paid_percent || 0),
    })), [data]);

    const daily = useMemo(() => (data?.daily || []).map((item) => ({
        ...item,
        total_fms: Number(item.total_fms || 0),
        total_amount: Number(item.total_amount || 0),
    })), [data]);

    const cityParticipation = useMemo(() => (data?.cityParticipation || []).map((item) => ({
        ...item,
        name: item.name || item.city || "Sin ciudad",
        total_fms: Number(item.total_fms || 0),
        total_amount: Number(item.total_amount || 0),
        participation_qty: Number(item.participation_qty || 0),
        participation_amount: Number(item.participation_amount || 0),
        overdue_amount: Number(item.overdue_amount || 0),
        paid_amount: Number(item.paid_amount || 0),
        overdue_percent: Number(item.overdue_percent || 0),
        paid_percent: Number(item.paid_percent || 0),
    })), [data]);

    const sedeParticipation = useMemo(() => (data?.sedeParticipation || []).map((item) => ({
        ...item,
        name: normalizeChartName(item.name || item.sede || "Sin sede"),
        sede: item.sede || item.name || "Sin sede",
        total_fms: Number(item.total_fms || 0),
        total_amount: Number(item.total_amount || 0),
        participation_qty: Number(item.participation_qty || 0),
        participation_amount: Number(item.participation_amount || 0),
        overdue_amount: Number(item.overdue_amount || 0),
        paid_amount: Number(item.paid_amount || 0),
        overdue_percent: Number(item.overdue_percent || 0),
        paid_percent: Number(item.paid_percent || 0),
    })), [data]);

    const advisorParticipation = useMemo(() => advisors.map((item) => ({
        ...item,
        name: item.advisor_chart_name || normalizeChartName(item.advisor_name),
        participation_amount: Number(item.participation_amount || 0),
        participation_qty: Number(item.participation_qty || 0),
        total_fms: Number(item.total_fms || 0),
        total_amount: Number(item.total_amount || 0),
        overdue_amount: Number(item.overdue_amount || 0),
        paid_amount: Number(item.paid_amount || 0),
        overdue_percent: Number(item.overdue_percent || 0),
        paid_percent: Number(item.paid_percent || 0),
    })), [advisors]);

    const hasAdvisorParticipation = advisorParticipation.length > 0;

    const moraByType = useMemo(() => (mora?.byType || []).map((item) => ({
        ...item,
        name: item.moraTypeName,
        overdueAmount: Number(item.overdueAmount || 0),
        overdueClients: Number(item.overdueClients || 0),
        moraPercent: Number(item.moraPercent || 0),
        maxDaysOverdue: Number(item.maxDaysOverdue || 0),
    })), [mora]);

    const moraByAdvisor = useMemo(() => (mora?.byAdvisor || []).map((item) => ({
        ...item,
        advisorChartName: normalizeChartName(item.advisorName),
        overdueAmount: Number(item.overdueAmount || 0),
        overdueClients: Number(item.overdueClients || 0),
    })), [mora]);

    const moraClients = mora?.clients || [];
    const advisorHistoricalFms = data?.advisorFms || [];
    const kpis = data?.kpis || {};
    const moraKpis = mora?.kpis || {};
    const catalogs = data?.catalogs || {};
    const hasAdvisors = advisors.length > 0;
    const hasDaily = daily.length > 0;
    const hasCityParticipation = cityParticipation.length > 0;
    const hasSedeParticipation = sedeParticipation.length > 0;
    const hasMora = moraByType.some((item) => item.overdueAmount > 0 || item.overdueClients > 0);
    const canSeeGlobalMora = Boolean(mora?.canSeeGlobalMora);
    const radialData = [{ name: "Mora", value: Number(moraKpis.totalMoraPercent || 0), fill: moraKpis.risk?.key === "high" ? "#dc2626" : moraKpis.risk?.key === "medium" ? "#f97316" : "#16a34a" }];

    return (
        <div>
            {loading && <PhoneLoader />}
            <ScrollToTop />
            <Header user={user} namePage="fmstatsPage" />

            <section className="sectionBody sectionBodyFmStats">
                <div className="containerCustom containerBody">
                    <div className="blockBody">
                        <div className="fmStatsHeader">
                            <div>
                                <span className="fmStatsEyebrow">Análisis comercial y mora</span>
                                <h2>Estadísticas de FM por asesor</h2>
                                <p>Control profesional de financiamientos, evolución diaria, cartera vencida, mora semanal, quincenal y mensual.</p>
                            </div>
                            <div className="fmStatsActions">
                                {data?.canExportExcel ? <button className="fmStatsBtn primary" onClick={exportExcel} disabled={exporting} type="button">{exporting ? "Exportando..." : "Descargar Excel"}</button> : null}
                                {!isAdvisorRole ? <button className="fmStatsBtn" onClick={resetFilters} type="button">Limpiar filtros</button> : null}
                            </div>
                        </div>

                        {!isAdvisorRole ? (
                            <div className="fmStatsFilters">
                                <div className="fmFilterItem"><label>Rango de fechas</label><input ref={calendarInputRef} className="fmDateInput" type="text" placeholder="Seleccionar rango de fechas" autoComplete="off" /></div>
                                {canSeeGlobalMora ? <div className="fmFilterItem"><label>Asesor</label><Select styles={selectStyles} menuPortalTarget={document.body} value={filters.advisorId} options={catalogs.advisors || []} onChange={(v) => handleChange("advisorId", v)} isClearable placeholder="Todos" /></div> : null}
                                <div className="fmFilterItem"><label>Ciudad</label><Select styles={selectStyles} menuPortalTarget={document.body} value={filters.city} options={catalogs.cities || []} onChange={(v) => handleChange("city", v)} isClearable placeholder="Todas" /></div>
                                <div className="fmFilterItem"><label>Sede</label><Select styles={selectStyles} menuPortalTarget={document.body} value={filters.sedeId} options={catalogs.sedes || []} onChange={(v) => handleChange("sedeId", v)} isClearable placeholder="Todas" /></div>
                                <div className="fmFilterItem"><label>Segmento</label><Select styles={selectStyles} menuPortalTarget={document.body} value={filters.segment} options={catalogs.segments || []} onChange={(v) => handleChange("segment", v)} isClearable placeholder="Todos" /></div>
                                <div className="fmFilterItem"><label>Orden</label><Select styles={selectStyles} menuPortalTarget={document.body} value={filters.orderBy} options={ORDER_OPTIONS} onChange={(v) => handleChange("orderBy", v || ORDER_OPTIONS[0])} placeholder="Ordenar por" /></div>
                            </div>
                        ) : (
                            <div className="fmAdvisorNotice">
                                <strong>Vista de asesor</strong>
                                <span>Mostrando todos tus FM históricos, ordenados desde el más antiguo al más reciente.</span>
                            </div>
                        )}

                        {loading ? <div className="fmStatsLoading"><PreloadFetch /></div> : (
                            <>
                                <div className="fmKpiGrid">
                                    <KpiCard title="Total de FM" value={Number(kpis.totalFms || 0)} subValue={`Crecimiento: ${Number(kpis.growthFms || 0)}%`} variant="blue" />
                                    <KpiCard title="Cartera total" value={PEN.format(Number(kpis.totalAmount || 0))} subValue={`Crecimiento: ${Number(kpis.growthAmount || 0)}%`} variant="green" />
                                    <KpiCard title="Monto cancelado" value={PEN.format(Number(kpis.fmCancelledAmount || 0))} subValue={`${Number(kpis.fmCancelledInstallments || 0)} cuotas canceladas · ${getPercentText(kpis.fmCancelledPercent)}`} variant="blue" />
                                    <KpiCard title="Monto pendiente a cobrar" value={PEN.format(Number(kpis.fmPendingCollectionAmount || 0))} subValue={`${Number(kpis.fmPendingInstallments || 0)} cuotas pendientes · ${getPercentText(kpis.fmPendingCollectionPercent)}`} variant="red" />
                                    <KpiCard title="Ticket promedio" value={PEN.format(Number(kpis.avgTicket || 0))} subValue="Promedio por FM" variant="orange" />
                                    <KpiCard title="Asesores activos" value={Number(kpis.activeAdvisors || 0)} subValue="Con FM en el periodo" variant="purple" />
                                </div>

                                <div className="fmParticipationGrid">
                                    <div className="fmChartCard fmParticipationCard">
                                        <div className="fmChartCardHeader">
                                            <div>
                                                <h3>Participación en Mora por ciudad</h3>
                                                <p>Distribución del monto en mora por ciudad según los filtros seleccionados.</p>
                                            </div>
                                        </div>
                                        <div className="fmChartBox fmPieChartBox">
                                            {hasCityParticipation ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Tooltip content={<MoraParticipationTooltip />} />
                                                        <Legend verticalAlign="bottom" height={36} />
                                                        <Pie
                                                            data={cityParticipation}
                                                            cx="50%"
                                                            cy="48%"
                                                            labelLine={false}
                                                            label={renderCustomizedPieLabel}
                                                            outerRadius={108}
                                                            fill="#dc2626"
                                                            dataKey="overdue_amount"
                                                            nameKey="name"
                                                        >
                                                            {cityParticipation.map((_, i) => <Cell key={`city-pie-${i}`} fill={COLORS[i % COLORS.length]} />)}
                                                        </Pie>
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            ) : <EmptyChart text="No hay participación por ciudad con los filtros seleccionados." />}
                                        </div>
                                    </div>

                                    <div className="fmChartCard fmParticipationCard">
                                        <div className="fmChartCardHeader">
                                            <div>
                                                <h3>Participación de Mora por sede</h3>
                                                <p>Comparativo por sede entre monto en mora y pagado sobre el total de FM.</p>
                                            </div>
                                        </div>
                                        <div className="fmChartBox fmLayerBarBox">
                                            {hasSedeParticipation ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={sedeParticipation} margin={{ top: 12, right: 20, left: 0, bottom: 70 }}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" />
                                                        <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                                                        <Tooltip content={<MoraParticipationTooltip />} />
                                                        <Legend wrapperStyle={{ fontSize: 11 }} />

                                                        <Bar yAxisId="left" dataKey="overdue_amount" name="Monto Mora" radius={[10, 10, 0, 0]} fill="#dc2626" />
                                                        <Bar yAxisId="left" dataKey="paid_amount" name="Monto pagado" radius={[10, 10, 0, 0]} fill="#16a34a" />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            ) : <EmptyChart text="No hay participación por sede con los filtros seleccionados." />}
                                        </div>
                                    </div>
                                </div>

                                <div className="fmAdvisorParticipationFull">
                                    <div className="fmChartCard fmAdvisorParticipationCard">
                                        <div className="fmChartCardHeader">
                                            <div>
                                                <h3>Participación por Mora de asesor</h3>
                                                <p>
                                                    Comparativo por asesor entre monto en mora y pagado sobre el total de FM,
                                                    según los filtros seleccionados.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="fmChartBox fmAdvisorLayerBarBox">
                                            {hasAdvisorParticipation ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                        data={advisorParticipation}
                                                        margin={{ top: 12, right: 24, left: 0, bottom: 85 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis
                                                            dataKey="name"
                                                            tick={{ fontSize: 11 }}
                                                            interval={0}
                                                            angle={-25}
                                                            textAnchor="end"
                                                            height={90}
                                                        />
                                                        <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                                                        <Tooltip content={<MoraParticipationTooltip />} />
                                                        <Legend wrapperStyle={{ fontSize: 11 }} />

                                                        <Bar yAxisId="left" dataKey="overdue_amount" name="Monto Mora" radius={[10, 10, 0, 0]} fill="#dc2626" />
                                                        <Bar yAxisId="left" dataKey="paid_amount" name="Monto pagado" radius={[10, 10, 0, 0]} fill="#16a34a" />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <EmptyChart text="No hay participación por asesor con los filtros seleccionados." />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="moraPanel">
                                    <div className="moraPanelHeader">
                                        <div>
                                            <span className="moraEyebrow">Gestión de mora · {mora?.asOfDate || "hoy"}</span>
                                            <h3>{canSeeGlobalMora ? "Mora global FM" : "Mi mora FM"}</h3>
                                            <p>{mora?.target?.label || "Mantener la mora bajo control operativo."}</p>
                                        </div>
                                        <div className={`moraRiskPill ${getRiskClass(moraKpis.risk)}`}>{moraKpis.risk?.label || "Controlado"}</div>
                                    </div>

                                    <div className="moraKpiGrid">
                                        <KpiCard title="Cartera total" value={PEN.format(Number(moraKpis.totalPortfolio || 0))} subValue={`${Number(moraKpis.totalClients || 0)} clientes activos`} variant="blue" badge="Base" />
                                        <KpiCard title="Monto total en mora" value={PEN.format(Number(moraKpis.totalOverdueAmount || 0))} subValue={`${Number(moraKpis.totalMoraPercent || 0)}% de la cartera`} variant="red" badge="Mora" />
                                        <KpiCard title="Monto cancelado" value={PEN.format(Number(moraKpis.fmCancelledAmount || 0))} subValue={`${Number(moraKpis.fmCancelledInstallments || 0)} cuotas canceladas · ${getPercentText(moraKpis.fmCancelledPercent)}`} variant="green" badge="Cancelado" />
                                        <KpiCard
                                            title="Monto pendiente a cobrar"
                                            value={PEN.format(Number(moraKpis.fmPendingCollectionAmount || 0))}
                                            subValue={`${Number(moraKpis.fmPendingInstallments || 0)} cuotas pendientes · ${getPercentText(moraKpis.fmPendingCollectionPercent)}`}
                                            variant="red"
                                            badge="Pendiente"
                                        />
                                        <KpiCard title="Clientes en mora" value={Number(moraKpis.totalOverdueClients || 0)} subValue={`${Number(moraKpis.maxDaysOverdue || 0)} días máx. atraso`} variant="orange" badge="Clientes" />
                                        <KpiCard title="Compromisos" value={Number(moraKpis.totalCommitments || 0)} subValue={`${Number(moraKpis.pendingCommitments || 0)} pendientes · ${Number(moraKpis.achievedCommitments || 0)} cumplidos`} variant="purple" badge="Gestión" />
                                    </div>

                                    <div className="moraChartsGrid">
                                        <div className="fmChartCard moraMainChart">
                                            <h3>Mora por tipo de frecuencia</h3>
                                            <div className="fmChartBox">
                                                {hasMora ? <ResponsiveContainer width="100%" height="100%"><BarChart data={moraByType}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis /><Tooltip content={<CustomTooltip />} /><Legend /><Bar dataKey="overdueAmount" name="Monto en mora" radius={[10, 10, 0, 0]}>{moraByType.map((item) => <Cell key={item.moraType} fill={MORA_COLORS[item.moraType] || "#2563eb"} />)}</Bar><Bar dataKey="overdueClients" name="Clientes" radius={[10, 10, 0, 0]} fill="#0f172a" /></BarChart></ResponsiveContainer> : <EmptyChart text="No se encontraron cuotas vencidas." />}
                                            </div>
                                        </div>

                                        <div className="fmChartCard moraGaugeCard">
                                            <h3>Porcentaje de mora vs meta</h3>
                                            <div className="moraGaugeWrap">
                                                <ResponsiveContainer width="100%" height={250}>
                                                    <RadialBarChart innerRadius="72%" outerRadius="100%" data={radialData} startAngle={180} endAngle={0}>
                                                        <RadialBar minAngle={15} background clockWise dataKey="value" cornerRadius={18} />
                                                    </RadialBarChart>
                                                </ResponsiveContainer>
                                                <div className="moraGaugeCenter"><strong>{Number(moraKpis.totalMoraPercent || 0)}%</strong><span>Meta máxima: {Number(mora?.target?.maxPercent || 10)}%</span></div>
                                            </div>
                                        </div>
                                    </div>

                                    {canSeeGlobalMora ? (
                                        <div className="fmChartCard moraAdvisorChart">
                                            <h3>Ranking de mora por asesor</h3>
                                            <div className="fmChartBox">
                                                {moraByAdvisor.length ? <ResponsiveContainer width="100%" height="100%"><BarChart data={moraByAdvisor}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="advisorChartName" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={80} /><YAxis /><Tooltip content={<CustomTooltip />} /><Bar dataKey="overdueAmount" name="Monto en mora" radius={[10, 10, 0, 0]}>{moraByAdvisor.map((_, i) => <Cell key={`advisor-mora-${i}`} fill={COLORS[i % COLORS.length]} />)}</Bar></BarChart></ResponsiveContainer> : <EmptyChart text="No hay asesores con mora en los filtros seleccionados." />}
                                            </div>
                                        </div>
                                    ) : null}

                                    <div className="moraTypeGrid">
                                        {moraByType.map((item) => <div key={item.moraType} className={`moraTypeCard ${getRiskClass(item.risk)}`}><span>{item.moraTypeName}</span><strong>{PEN.format(item.overdueAmount)}</strong><p>{item.overdueClients} clientes · {item.moraPercent}% cartera · {item.maxDaysOverdue} días máx.</p></div>)}
                                    </div>
                                </div>

                                <div className="fmTopAdvisors">
                                    {(data?.topAdvisors || []).map((advisor) => <div className="fmTopAdvisorCard" key={advisor.advisor_id}><span>TOP {advisor.rank}</span><strong>{advisor.advisor_name}</strong><p>{Number(advisor.total_fms || 0)} FMs · {PEN.format(Number(advisor.total_amount || 0))}</p></div>)}
                                </div>

                                <div className="fmChartsGrid">
                                    <div className="fmChartCard"><h3>FM por asesor - cantidad</h3><div className="fmChartBox">{hasAdvisors ? <ResponsiveContainer width="100%" height="100%"><BarChart data={advisors}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="advisor_chart_name" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={90} /><YAxis allowDecimals={false} /><Tooltip content={<CustomTooltip />} /><Bar dataKey="total_fms" name="FMs" radius={[10, 10, 0, 0]}>{advisors.map((_, i) => <Cell key={`qty-${i}`} fill={COLORS[i % COLORS.length]} />)}</Bar></BarChart></ResponsiveContainer> : <EmptyChart />}</div></div>
                                    <div className="fmChartCard"><h3>Monto financiado por asesor</h3><div className="fmChartBox">{hasAdvisors ? <ResponsiveContainer width="100%" height="100%"><BarChart data={advisors}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="advisor_chart_name" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={90} /><YAxis /><Tooltip content={<CustomTooltip />} /><Bar dataKey="total_amount" name="Monto financiado" radius={[10, 10, 0, 0]}>{advisors.map((_, i) => <Cell key={`amount-${i}`} fill={COLORS[(i + 2) % COLORS.length]} />)}</Bar></BarChart></ResponsiveContainer> : <EmptyChart />}</div></div>
                                    <div className="fmChartCard fmChartWide"><h3>Evolución diaria de FM</h3><div className="fmChartBox">{hasDaily ? <ResponsiveContainer width="100%" height="100%"><AreaChart data={daily}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} /><Tooltip content={<CustomTooltip />} /><Area type="monotone" dataKey="total_fms" name="FMs" stroke="#2563eb" fill="#dbeafe" strokeWidth={3} /><Line type="monotone" dataKey="total_fms" name="FMs" stroke="#2563eb" strokeWidth={3} dot={{ r: 4 }} /></AreaChart></ResponsiveContainer> : <EmptyChart />}</div></div>
                                </div>

                                {isAdvisorRole ? (
                                    <div className="moraClientsCard">
                                        <div className="moraTableHeader">
                                            <div>
                                                <h3>Mis FM históricos</h3>
                                                <p>Listado completo desde siempre, ordenado del FM más antiguo al más reciente.</p>
                                            </div>
                                        </div>
                                        <div className="moraTableWrap">
                                            <table className="moraTable">
                                                <thead>
                                                    <tr>
                                                        <th>Fecha</th>
                                                        <th>Cliente</th>
                                                        <th>Equipo</th>
                                                        <th>Segmento</th>
                                                        <th>Monto</th>
                                                        <th>Cuota</th>
                                                        <th>Sede</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {advisorHistoricalFms.length ? advisorHistoricalFms.map((fm) => (
                                                        <tr key={fm.fmId}>
                                                            <td><b>{fm.date || "-"}</b></td>
                                                            <td className="dataName"><strong>{fm.clientName}</strong><span>DNI {fm.dni || "-"} · {fm.phoneNumber || "Sin teléfono"}</span></td>
                                                            <td><b>{fm.device || "-"}</b></td>
                                                            <td><em className={`moraTypeBadge ${String(fm.segmentName || "").toLowerCase()}`}>{fm.segmentName}</em></td>
                                                            <td><b>{PEN.format(Number(fm.amount || 0))}</b></td>
                                                            <td><b>{PEN.format(Number(fm.fee || 0))}</b><span>{Number(fm.numFee || 0)} cuotas</span></td>
                                                            <td>{fm.sede || "-"}</td>
                                                        </tr>
                                                    )) : (
                                                        <tr><td colSpan="7"><EmptyChart text="No se encontraron FM históricos para este asesor." /></td></tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ) : null}

                                <div className="fmAdvisorGrid">
                                    {advisors.map((advisor) => <div className={`fmAdvisorCard performance-${advisor.performance}`} key={advisor.advisor_id}><div className="fmAdvisorRank">#{advisor.rank}</div><div className="fmAdvisorInfo"><strong>{advisor.advisor_name}</strong><span>{advisor.total_fms} FM realizados</span></div><div className="fmAdvisorMetrics"><p><b>{PEN.format(advisor.total_amount)}</b><span>Monto financiado</span></p><p><b>{PEN.format(advisor.avg_ticket)}</b><span>Ticket promedio</span></p><p><b>{advisor.participation_amount}%</b><span>Participación</span></p></div></div>)}
                                </div>

                                <div className="moraClientsCard">
                                    <div className="moraTableHeader"><div><h3>Seguimiento de clientes con atraso</h3><p>{savingCommitment ? "Guardando compromiso..." : "Registra compromisos de pago y prioriza por días de atraso."}</p></div></div>
                                    <div className="moraTableWrap">
                                        <table className="moraTable">
                                            <thead><tr><th>Cliente</th><th>Asesor</th><th>Tipo</th><th>Mora</th><th>Atraso</th><th>Compromiso</th><th>Gestión</th></tr></thead>
                                            <tbody>
                                                {moraClients.length ? moraClients.map((client) => <tr key={client.fmId}><td className="dataName"><strong>{client.clientName}</strong><span>DNI {client.dni} · {client.phoneNumber || "Sin teléfono"}</span><small>{client.device}</small></td><td>{client.advisorName}<span>{client.sede}</span></td><td><em className={`moraTypeBadge ${client.moraType}`}>{client.moraTypeName}</em></td><td><b>{PEN.format(Number(client.overdueAmount || 0))}</b><span>{client.overdueInstallments} cuotas</span></td><td><b>{client.daysOverdue} días</b><span>Desde {client.oldestDueDate || "-"}</span></td><td>{client.commitmentDate ? <><b>{client.commitmentDate}</b><span>{PEN.format(Number(client.commitmentAmount || 0))} · {client.commitmentStatus}</span><small>{client.commitmentNote}</small></> : <span>Sin compromiso</span>}</td><td><CommitmentForm client={client} onSave={saveCommitment} /></td></tr>) : <tr><td colSpan="7"><EmptyChart text="No hay clientes con mora para mostrar." /></td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
